import { Outcome } from "@ethossoftworks/outcome"
import { Bloc } from "@lib/bloc/Bloc"
import { PDFAsset } from "@lib/pdf/models/PDFAsset"
import { PDFAssetPart } from "@lib/pdf/models/PDFAssetPart"
import { PDFWorkOrder } from "@lib/pdf/models/PDFWorkOrder"
import { Strings } from "@lib/Strings"
import { arrayToObject, safeParseFloat, safeParseInt, stringIsNotEmpty } from "@lib/TypeUtil"
import { Asset, pdfAssetFromModel } from "@model/assets/Asset"
import { AssetPart, pdfAssetPartFromModel } from "@model/assets/AssetPart"
import { AssetSchedule } from "@model/assets/AssetSchedule"
import { PriorAssetUsage } from "@model/assets/PriorAssetUsage"
import { AsyncStatus } from "@model/AsyncStatus"
import { missingWorkOrderRequiredFields } from "@model/company/WorkOrderRequiredFieldsUtil"
import { Contact } from "@model/contacts/Contact"
import { HistoryItem } from "@model/HistoryItem"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { ServiceCode } from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { TagChange, tagChangeFromTag } from "@model/Tag"
import { UserData } from "@model/user/User"
import { pdfWorkOrderFromModels, WorkOrder, WorkOrderStatus } from "@model/workOrders/WorkOrder"
import { WorkOrderPrintOption } from "@model/workOrders/WorkOrderPrintOption"
import { WorkRequest } from "@model/workRequests/WorkRequest"
import { AssetService } from "@service/assets/AssetService"
import { WorkOrderService } from "@service/workOrders/WorkOrderService"
import { workRequestToServiceItemForm } from "@service/workRequests/MainWorkRequestService"
import { WorkRequestService } from "@service/workRequests/WorkRequestService"
import { CompanyBloc } from "@state/company/CompanyBloc"
import { UserBloc } from "@state/user/UserBloc"
import { ServiceItemsBloc } from "@state/workRequests/ServiceItemsBloc"
import isEqual from "lodash.isequal"

export type CreateNewWorkOrderScreenState = {
    isVisible: boolean
    isAfterSaveVisible: boolean
    effectStatus: Record<WorkOrderEffect, AsyncStatus>
    workOrder: null | WorkOrder
    asset: null | Asset
    form: WorkOrderForm
    workRequests: WorkRequest[]
    hasBeenSaved: boolean
    formHasChanged: boolean
    isSaving: boolean
    isLoading: boolean
    isEditMode: boolean
    isEditModeLoading: boolean
    isFormValid: boolean
    ReassignWorkOrderModalIsVisible: boolean
    AssetParts?: AssetPart[]
    AssetSchedule: AssetSchedule[]
    showPrintList: boolean
    workOrderToPrint: number | null
    workOrderToPrintIsLoading: boolean
    partsListIsLoading: boolean
    pdfAsset?: PDFAsset
    pdfWorkOrder?: PDFWorkOrder
    pdfAssetParts?: PDFAssetPart[]
    pdfCompanyName?: string
    selectedPrintOption?: WorkOrderPrintOption
    showPartsList: boolean
    showMeterSyncInfo: boolean
    LatestAssetUsage: PriorAssetUsage | null
    AssetUsagePriorToCompletion: PriorAssetUsage | null
    showOnHoldModal: boolean
    workOrderHistory: HistoryItem<WorkOrderStatus>[]
    completeModalOpen: boolean
    closingModalOpen: boolean
    workOrderDismissal: {
        reason: string
        workRequestsToDelete: number[]
    } | null
    closedWorkOrderEditMode: boolean
    showSyncMeter: boolean
    syncModalHasBeenShown: boolean
    showEmptyWorkOrderModal: boolean
    emptyWorkOrderModalHasBeenConfirmed: boolean
    emptyWorkOrderModalDoNotShowAgain: boolean
    callerFromMobileApp: string | null
}

export type WorkOrderForm = {
    workOrderId: number | null
    status: WorkOrderStatus
    assignedTo: number
    urgency: boolean
    dueDate: Date | null
    // Action Item #7751: WO-level Due meter values, always editable.
    dueHourMeter: string | null
    dueOdometer: string | null
    physicalHourMeter: string | null
    physicalOdometer: string | null
    specialInstructions: string
    notifyContacts: number[]
    workPerformedById: number[]
    openedDate: Date | null
    site: Site | null
    completionDate: Date | null
    onHold: boolean
    onHoldReason: string | null
    meterSyncRequired: boolean
    createdByID: number | null
    createdByUserData: UserData | null
}

export enum WorkOrderEffect {
    Fetch = "fetch",
    Save = "save",
    SaveRequest = "saveRequest",
    FetchWorkOrderHistory = "fetchWorkOrderHistory",
}

export class CreateNewWorkOrderScreenBloc extends Bloc<CreateNewWorkOrderScreenState> {
    constructor(
        private workOrderService: WorkOrderService,
        private assetService: AssetService,
        private workRequestService: WorkRequestService,
        private serviceItemsBloc: ServiceItemsBloc,
        private companyBloc: CompanyBloc,
        private userBloc: UserBloc
    ) {
        super(newWorkOrderViewState(), { persistStateOnDispose: false })
    }

    /**
     * Computes derived state properties.
     *
     * IMPORTANT: We pass `state` explicitly to the helper methods because `this.state`
     * still contains the OLD state when `computed` is called. The Bloc's update() method
     * calls computed() BEFORE updating this._state, so using this.state here would cause
     * formHasChanged to always be one update behind (requiring two changes to trigger).
     */
    override computed = (state: CreateNewWorkOrderScreenState): Partial<CreateNewWorkOrderScreenState> => {
        return {
            formHasChanged: this.formHasChanged(state),
            isFormValid: this.isFormValidCondition(state),
        }
    }

    /**
     * Checks if the work order form has changed from its original state.
     * @param state - Optional state to use. When called from computed(), pass the new state.
     *                When called elsewhere, defaults to this.state.
     */
    formHasChanged = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state.workOrder !== null &&
            (!isEqual(
                {
                    ...state.form,
                    meterSyncRequired: false,
                },
                newWorkOrderForm(state.workOrder, null, this.companyBloc.state.allSites)
            ) ||
                this.serviceItemsBloc.serviceItemsHasChanged())
        )
    }

    /**
     * Checks if the form is valid for saving.
     * @param state - Optional state to use. Defaults to this.state.
     */
    isFormValidCondition = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state.form.specialInstructions.trim().length != 0 &&
            state?.asset != null &&
            (this.workCompletedStatusCondition(state) ||
                this.ClosingStatusCondition(state) ||
                this.ClosedEditionStatusCondition(state) ||
                state.form.status == WorkOrderStatus.Pending ||
                state.form.status == WorkOrderStatus.InProgress ||
                state.form.status == WorkOrderStatus.Open)
        )
    }

    workCompletedStatusCondition = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state.form.status == WorkOrderStatus.WorkCompleted &&
            state.form.completionDate != null &&
            this.areFormMetersValid(state) &&
            this.areWorkOrderRequiredFieldsComplete(state)
        )
    }

    ClosingStatusCondition = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state.form.status == WorkOrderStatus.Closed &&
            state.form.completionDate != null &&
            this.serviceItemsBloc.areAllServiceItemsInDoneStatus() &&
            this.areFormMetersValid(state) &&
            this.areWorkOrderRequiredFieldsComplete(state)
        )
    }

    /**
     * The company's additional Work Order requirements, checked alongside the Completion Date and
     * meter checks. Service Item requirements are enforced where an item is completed, not here.
     */
    areWorkOrderRequiredFieldsComplete = (state: CreateNewWorkOrderScreenState = this.state): boolean =>
        this.missingWorkOrderRequiredFields(state).length === 0

    missingWorkOrderRequiredFields = (state: CreateNewWorkOrderScreenState = this.state) =>
        missingWorkOrderRequiredFields(
            state.form,
            this.companyBloc.state.settings.workOrderRequiredFields.workOrderFields
        )

    ClosedEditionStatusCondition = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state.form.status == WorkOrderStatus.Closed &&
            state.workOrder?.status == WorkOrderStatus.Closed &&
            state.form.completionDate != null
        )
    }

    areFormMetersValid = (state: CreateNewWorkOrderScreenState = this.state): boolean => {
        return (
            state?.asset != null &&
            (!state.asset.displayHourMeter ||
                (state.asset.displayHourMeter && stringIsNotEmpty(state.form.physicalHourMeter))) &&
            (!state.asset.displayOdometer ||
                (state.asset.displayOdometer && stringIsNotEmpty(state.form.physicalOdometer)))
        )
    }

    Init = () => {
        this.resetForm()
        this.LoadInitialData()
        this.show()
    }

    addWorkRequestsAsServiceItems = async (workRequests: WorkRequest[]) => {
        const items = workRequests.map((wr, idx) => workRequestToServiceItemForm(wr, 1000 + idx))
        for (const item of items) {
            await this.serviceItemsBloc.addExistingItem(item, true)
        }
    }

    LoadInitialData() {
        if (!this.state.isEditMode) {
            var user = this.userBloc.state.user
            //For the workPerformedBy and AssignedTo fields, we will set the current user Contact if they are a mechanic
            if (user.personID && user.isMechanic) {
                this.assignedToChanged(user.personID)
                this.workPerformedByChanged([user.personID])
                //When opening a new work order since we have a default assigned to, we also set the status as open
                this.statusChanged(WorkOrderStatus.Open)
            }
        }
    }

    setEditMode = async (workOrderId: number, assetId: number) => {
        this.update({
            isVisible: true,
            isEditMode: true,
            isEditModeLoading: true,
            syncModalHasBeenShown: false,
            showSyncMeter: false,
        })

        const [
            workOrderOutcome,
            assetOutcome,
            workRequestsOutcome,
            assetTagsOutcome,
            LatestAssetUsageOutcome,
            assetScheduleOutcome,
        ] = await Promise.all([
            this.workOrderService.fetchWorkOrder(workOrderId),
            this.assetService.fetchAsset(assetId),
            this.workRequestService.fetchWorkRequestsForWorkOrder(workOrderId),
            this.assetService.fetchAssetTags(assetId), // Fetch asset tags to show in the asset identifier
            this.assetService.fetchAssetUsage(assetId, new Date()), // Fetch latest asset usage to show in the asset identifier
            this.assetService.fetchAssetSchedule(assetId), // Asset ↔ ServiceScheduleItem mappings, used to gate the step dropdown in ServiceItem
        ])

        if (assetScheduleOutcome.isOk()) {
            this.update({ AssetSchedule: assetScheduleOutcome.value })
        } else {
            this.update({ AssetSchedule: [] })
        }

        if (workOrderOutcome.isOk()) {
            this.update({
                form: newWorkOrderForm(
                    workOrderOutcome.value,
                    assetOutcome.isOk() ? assetOutcome.value : null,
                    this.companyBloc.state.allSites
                ),
                workOrder: workOrderOutcome.value,
            })

            this.workOrderToPrintChanged(workOrderOutcome.value.id)
        }

        if (assetOutcome.isOk()) {
            this.update({ asset: assetOutcome.value })

            if (assetTagsOutcome.isOk()) {
                this.update({
                    asset: { ...(this.state.asset as Asset), AssetTag: assetTagsOutcome.value },
                })
            }

            if (LatestAssetUsageOutcome.isOk()) {
                this.update({
                    LatestAssetUsage: LatestAssetUsageOutcome.value,
                })
            }
            if (this.state.form.completionDate) {
                await this.updateAssetUsagePriorToCompletionDate(assetId, this.state.form.completionDate)
            }
        }

        if (workRequestsOutcome.isError()) {
            this.update({ isEditModeLoading: false })
            throw workRequestsOutcome.error
        }

        const items = workRequestsOutcome.value.map((wr, idx) => workRequestToServiceItemForm(wr, 1000 + idx))

        // Follow-ups are not in the work order's items list (they may have workOrderId = null
        // or be assigned to a different work order). Fetch all non-dismissed work requests
        // for the asset to resolve the forward follow-up links.
        const assetWRsOutcome = await this.workRequestService.fetchNotDismissedWorkRequestsForAsset(assetId)
        if (assetWRsOutcome.isOk()) {
            const assetItems = assetWRsOutcome.value.map((wr) => workRequestToServiceItemForm(wr, 0))
            resolveChildFollowUpIds(items, assetItems)
        } else {
            resolveChildFollowUpIds(items)
        }

        for (const item of items) {
            await this.serviceItemsBloc.addExistingItem(item, true)
        }

        this.update({ isEditModeLoading: false })
    }

    /**
     * Sets childFollowUpId on the original service item after a follow-up is created,
     * so the "View Follow-Up" button appears immediately.
     */
    updateItemChildFollowUpId = (originalItemId: number, followUpId: number) => {
        const item = this.serviceItemsBloc.state.items.find((i) => i.id === originalItemId)
        if (!item) return
        const bloc = this.serviceItemsBloc.getItemBloc(item.uiElementId)
        bloc?.updateField("childFollowUpId", followUpId)
        this.serviceItemsBloc.updateItem({ ...item, childFollowUpId: followUpId })
    }

    refreshServiceItemsAfterFollowUpCreated = async () => {
        if (!this.state.workOrder) return
        const workRequestsOutcome = await this.workRequestService.fetchWorkRequestsForWorkOrder(this.state.workOrder.id)
        if (workRequestsOutcome.isError()) return
        this.serviceItemsBloc.clearAll(false)
        const items = workRequestsOutcome.value.map((wr, idx) => workRequestToServiceItemForm(wr, 1000 + idx))
        resolveChildFollowUpIds(items)
        for (const item of items) {
            await this.serviceItemsBloc.addExistingItem(item, true)
        }
    }

    updateAssetUsagePriorToCompletionDate = async (assetId: number, completionDate: Date) => {
        if (
            this.state.workOrder?.status === WorkOrderStatus.Closed ||
            this.state.workOrder?.status === WorkOrderStatus.WorkCompleted
        ) {
            const [AssetUsagePriorToCompletionOutcome] = await Promise.all([
                this.assetService.fetchAssetUsage(assetId, completionDate), // Fetch latest asset usage to show in the asset identifier
            ])

            if (AssetUsagePriorToCompletionOutcome.isOk()) {
                this.update({
                    AssetUsagePriorToCompletion: AssetUsagePriorToCompletionOutcome.value,
                })
            }
        }
    }

    saveForm = (serviceItems?: ServiceItemForm[]) =>
        this.effect({
            id: WorkOrderEffect.Save,
            block: async (job) => {
                if (!this.state.asset) return Outcome.error(undefined)

                let refreshSiteCache = this.state.form.site?.id == 0

                var response = await job.pause(
                    this.workOrderService.upsertWorkOrderWithServiceItems(
                        this.state.asset.id,
                        {
                            workOrderId: this.state.form.workOrderId,
                            assignedTo: this.state.form.assignedTo,
                            specialInstructions: this.state.form.specialInstructions,
                            status: this.state.form.status,
                            isUrgent: this.state.form.urgency, //to be changed
                            dueDate: this.state.form.dueDate,
                            dueHourMeter: this.state.form.dueHourMeter ? this.state.form.dueHourMeter : "",
                            dueOdometer: this.state.form.dueOdometer ? this.state.form.dueOdometer : "",
                            notifyContacts: this.state.form.notifyContacts,
                            workPerformedById: this.state.form.workPerformedById,
                            workCompletedDate: this.state.form.completionDate,
                            completedOdometer: this.state.form.physicalOdometer ? this.state.form.physicalOdometer : "",
                            completedHourMeter: this.state.form.physicalHourMeter
                                ? this.state.form.physicalHourMeter
                                : "",
                            site: this.state.form.site,
                            onHold: this.state.form.onHold,
                            onHoldReason: this.state.form.onHoldReason,
                            meterSyncRequired: this.state.form.meterSyncRequired,
                        },
                        serviceItems != undefined ? serviceItems : []
                    )
                )

                if (response.isError()) {
                    return Outcome.error(response)
                }

                let workOrder = response.value

                if (!workOrder) return Outcome.error(undefined)

                if (refreshSiteCache) {
                    await this.companyBloc.fetchAllSites()
                }

                this.update({
                    workOrder: workOrder,
                    form: newWorkOrderForm(workOrder, this.state.asset, this.companyBloc.state.allSites),
                })

                return Outcome.ok(response)
            },
        })

    fetchWorkOrderHistoryData = (workOrderId: number) =>
        this.effect({
            id: WorkOrderEffect.FetchWorkOrderHistory,
            block: async (job) => {
                this.effectStatusChanged(WorkOrderEffect.FetchWorkOrderHistory, AsyncStatus.busy())
                const workOrderHistory = await job.pause(this.workOrderService.fetchWorkOrderHistory(workOrderId))

                if (workOrderHistory.isError()) {
                    this.effectStatusChanged(WorkOrderEffect.FetchWorkOrderHistory, AsyncStatus.error(undefined))
                    return Outcome.error(undefined)
                }

                this.update({ workOrderHistory: workOrderHistory.value })

                this.effectStatusChanged(WorkOrderEffect.FetchWorkOrderHistory, AsyncStatus.idle())
                return Outcome.ok(undefined)
            },
        })

    printWorkOrder = async (
        workOrderPrintOption: WorkOrderPrintOption,
        contacts: Contact[],
        serviceCodes: ServiceCode[],
        sites: Site[]
    ) => {
        let pdfAsset: PDFAsset | null
        let pdfWorkOrder: PDFWorkOrder
        let pdfAssetParts: PDFAssetPart[] | undefined = undefined
        let pdfCompanyName: string = ""
        this.update({
            workOrderToPrintIsLoading: true,
        })
        //new create work order forms allows to print parts list in creation mode if asset is selected
        //In edition mode will also allow for printing work orders
        if (this.state.asset == null) return Outcome.error("Asset is null")

        if (this.state.workOrderToPrint) {
            const workOrderOutcome = await this.workOrderService.fetchWorkOrder(this.state.workOrderToPrint!)
            if (workOrderOutcome.isError()) {
                return workOrderOutcome
            }

            if (
                workOrderPrintOption == WorkOrderPrintOption.Summary ||
                workOrderPrintOption == WorkOrderPrintOption.WorkOrder ||
                workOrderPrintOption == WorkOrderPrintOption.SummaryWithParts ||
                workOrderPrintOption == WorkOrderPrintOption.WorkOrderWithParts
            ) {
                const workRequestsOutcome = await this.workRequestService.fetchWorkRequestsForWorkOrder(
                    workOrderOutcome.value.id
                )
                if (workRequestsOutcome.isError()) {
                    return workRequestsOutcome
                }
                // Action Item 6764: the work request fetch maps attachments as empty (the form
                // loads them lazily per Service Item), so hydrate them for the PDF's
                // Attachments section.
                await this.workRequestService.hydrateAttachments(workRequestsOutcome.value)
                pdfWorkOrder = pdfWorkOrderFromModels(
                    workOrderOutcome.value,
                    workRequestsOutcome.value,
                    contacts,
                    serviceCodes,
                    sites
                )
            } else {
                pdfWorkOrder = pdfWorkOrderFromModels(workOrderOutcome.value, [], contacts, serviceCodes, sites)
            }
            this.update({
                pdfWorkOrder: pdfWorkOrder,
            })
        }

        pdfAsset = pdfAssetFromModel(this.state.asset)
        pdfCompanyName = this.state.asset.companyName

        if (
            workOrderPrintOption == WorkOrderPrintOption.Parts ||
            workOrderPrintOption == WorkOrderPrintOption.SummaryWithParts ||
            workOrderPrintOption == WorkOrderPrintOption.WorkOrderWithParts
        ) {
            const assetPartsOutcome = await this.assetService.fetchParts(this.state.asset.id)
            if (assetPartsOutcome.isError()) {
                return assetPartsOutcome
            }

            pdfAssetParts = assetPartsOutcome.value.map(pdfAssetPartFromModel)
        }

        this.update({
            pdfAsset: pdfAsset,
            pdfAssetParts: pdfAssetParts,
            pdfCompanyName: pdfCompanyName,
            selectedPrintOption: workOrderPrintOption,
            workOrderToPrintIsLoading: false,
            workOrderToPrint: null, // This hides the printing options dialog
        })

        return Outcome.ok(undefined)
    }

    loadParts = async (assetId: number) => {
        this.update({
            partsListIsLoading: true,
        })

        const assetPartsOutcome = await this.assetService.fetchParts(assetId)
        if (assetPartsOutcome.isError()) {
            return assetPartsOutcome
        }

        this.update({
            AssetParts: assetPartsOutcome.value,
            partsListIsLoading: false,
        })

        return Outcome.ok(undefined)
    }

    fetchAssetTags = async (assetId: number) => {
        const assetTagsOutcome = await this.assetService.fetchAssetTags(assetId)
        if (assetTagsOutcome.isError()) {
            return assetTagsOutcome
        }

        this.update({
            asset: { ...(this.state.asset as Asset), AssetTag: assetTagsOutcome.value },
        })

        return Outcome.ok(undefined)
    }

    fetchAssetSchedule = async (assetId: number) => {
        const assetScheduleOutcome = await this.assetService.fetchAssetSchedule(assetId)
        if (assetScheduleOutcome.isError()) {
            this.update({ AssetSchedule: [] })
            return assetScheduleOutcome
        }

        this.update({ AssetSchedule: assetScheduleOutcome.value })

        return Outcome.ok(undefined)
    }

    fetchAssetUsage = async (assetId: number) => {
        const assetUsageOutcome = await this.assetService.fetchAssetUsage(assetId, new Date())
        if (assetUsageOutcome.isError()) {
            return assetUsageOutcome
        }

        this.update({
            LatestAssetUsage: assetUsageOutcome.value,
        })

        return Outcome.ok(undefined)
    }

    fetchAssetUsagePriorToCompletionDate = async (assetId: number, date: Date) => {
        const assetUsageOutcome = await this.assetService.fetchAssetUsage(assetId, date)
        if (assetUsageOutcome.isError()) {
            return assetUsageOutcome
        }

        this.update({
            AssetUsagePriorToCompletion: assetUsageOutcome.value,
        })

        return Outcome.ok(undefined)
    }

    show = () => this.update({ isVisible: true })

    hide = () => this.update({ isVisible: false })

    showAfterSave = () => this.update({ isAfterSaveVisible: true })
    hideAfterSave = () => this.update({ isAfterSaveVisible: false })

    showMeterSyncInfo = (show: boolean) => this.update({ showMeterSyncInfo: show })

    showPrintList = (show: boolean) => this.update({ showPrintList: show })

    workOrderToPrintChanged = (workOrderId: number | null) =>
        this.update({
            workOrderToPrint: workOrderId,
        })

    completeModalOpenChanged = (open: boolean) => this.update({ completeModalOpen: open })

    closingModalOpenChanged = (open: boolean) => this.update({ closingModalOpen: open })

    showPartsList = (show: boolean) => this.update({ showPartsList: show })

    statusChanged = (status: WorkOrderStatus) => this.formFieldChanged({ status: status })

    openedDateChanged = (openedDate: Date) => this.formFieldChanged({ openedDate: openedDate })

    assignedToChanged = (contactId: number) => this.formFieldChanged({ assignedTo: contactId })

    notifyContactsChanged = (contactIds: number[]) => this.formFieldChanged({ notifyContacts: contactIds })

    workPerformedByChanged = (contactIds: number[]) => this.formFieldChanged({ workPerformedById: contactIds })

    specialInstructionsChanged = (specialInstructions: string) =>
        this.formFieldChanged({ specialInstructions: specialInstructions })

    urgencyChanged = (urgency: boolean) => this.formFieldChanged({ urgency: urgency })

    siteChanged = (site: Site | null) => this.formFieldChanged({ site: site })

    completionDateChanged = (completionDate: Date | null) => this.formFieldChanged({ completionDate: completionDate })

    dueDateChanged = (dueDate: Date) => this.formFieldChanged({ dueDate: dueDate })

    // Bug #11217: keep a NEW Work Order's Due Date / Due Odometer / Due Hour Meter at the closest-to-due
    // value across its Service Items. Called as each item is added (never on remove); a no-op when
    // editing an existing Work Order so its stored due values are never touched.
    applyServiceItemDueValuesForNewWorkOrder = (item: ServiceItemForm) => {
        if (this.state.isEditMode) return

        const dueOdometerStr = this.state.form.dueOdometer
        const dueHourMeterStr = this.state.form.dueHourMeter
        const currentOdometer = dueOdometerStr != null && dueOdometerStr !== "" ? safeParseInt(dueOdometerStr) : null
        const currentHourMeter = dueHourMeterStr != null && dueHourMeterStr !== "" ? safeParseFloat(dueHourMeterStr) : null

        const dueDate = closerDueDate(this.state.form.dueDate, item.dueDate)
        const dueOdometer = closerDueValue(currentOdometer, item.dueOdometer)
        const dueHourMeter = closerDueValue(currentHourMeter, item.dueHourMeter)

        this.formFieldChanged({
            dueDate: dueDate,
            dueOdometer: dueOdometer != null ? Strings.formatInteger(dueOdometer) : this.state.form.dueOdometer,
            dueHourMeter: dueHourMeter != null ? Strings.formatDecimal(dueHourMeter) : this.state.form.dueHourMeter,
        })
    }

    // Action Item #7751: WO-level Due meter handlers.
    dueHourMeterChanged = (dueHourMeter: string) => this.formFieldChanged({ dueHourMeter: dueHourMeter })

    dueOdometerChanged = (dueOdometer: string) => this.formFieldChanged({ dueOdometer: dueOdometer })

    // Action Item #11164: a Closed Work Order's recorded meters are immutable — guarded here in
    // the bloc so programmatic callers (e.g. ClosingWorkOrderModal's onConfirm) can never write
    // them either. The close flow itself is unaffected: during closing the persisted status is
    // not yet Closed.
    physicalOdometerChanged = (physicalOdometer: string) => {
        if (this.state.workOrder?.status === WorkOrderStatus.Closed) return
        this.formFieldChanged({ physicalOdometer: physicalOdometer })
    }

    onHoldChanged = (onHold: boolean) => this.formFieldChanged({ onHold: onHold })
    onHoldReasonChanged = (onHoldReason: string) => this.formFieldChanged({ onHoldReason: onHoldReason })

    showOnHoldModalChanged = (show: boolean) => this.update({ showOnHoldModal: show })

    // Action Item #11164: same closed-WO meter guard as physicalOdometerChanged.
    physicalHourMeterChanged = (physicalHourMeter: string) => {
        if (this.state.workOrder?.status === WorkOrderStatus.Closed) return
        this.formFieldChanged({ physicalHourMeter: physicalHourMeter })
    }

    setIsSaving = (value: boolean) => this.update({ isSaving: value })

    setHasBeenSaved = (value: boolean) => this.update({ hasBeenSaved: value })

    setIsLoading = (value: boolean) => this.update({ isLoading: value, isEditModeLoading: value })

    setClosedWorkOrderEditMode = (value: boolean) => this.update({ closedWorkOrderEditMode: value })

    /**
     * Forces recalculation of computed properties (formHasChanged, isFormValid).
     * This is needed when service items change for Closed Work Orders because
     * the automatic status update is skipped for closed work orders.
     */
    recalculateFormState = () => this.update({})

    setReassignWorkOrderModalIsVisible = (value: boolean) => this.update({ ReassignWorkOrderModalIsVisible: value })

    private formFieldChanged = (form: Partial<WorkOrderForm>) =>
        this.update({
            form: {
                ...this.state.form,
                ...form,
            },
        })

    private effectStatusChanged = (effect: WorkOrderEffect, status: AsyncStatus) =>
        this.update({
            effectStatus: {
                ...this.state.effectStatus,
                [effect]: status,
            },
        })

    assetChanged = (asset: Asset | null) => {
        this.update({ asset: asset })

        if (asset) {
            this.fetchAssetTags(asset.id)
            this.fetchAssetUsage(asset.id)
            this.fetchAssetSchedule(asset.id)
        } else {
            this.update({ AssetSchedule: [] })
        }
    }

    loadDefaultSelectedAsset = async (assetId: number) => {
        this.update({ isLoading: true })
        const outcome = await this.assetService.fetchAsset(assetId)
        if (outcome.isOk()) {
            this.assetChanged(outcome.value)
        }
        this.update({ isLoading: false })
    }

    resetForm = () =>
        this.update({
            form: newWorkOrderForm(),
            asset: null,
            workOrder: null,
            workRequests: [],
            isEditMode: false,
            showMeterSyncInfo: false,
            syncModalHasBeenShown: false,
            showSyncMeter: false,
            showEmptyWorkOrderModal: false,
            emptyWorkOrderModalHasBeenConfirmed: false,
            emptyWorkOrderModalDoNotShowAgain: false,
            isSaving: false,
            isLoading: false,
            AssetSchedule: [],
        })

    dismissWorkOrder = async (reason: string, workRequestsToDelete: number[]) => {
        if (!this.state.workOrder?.id) {
            return Outcome.error(undefined)
        }
        const response = await this.workOrderService.deleteWorkOrder(
            this.state.workOrder?.id,
            reason,
            workRequestsToDelete
        )
        if (response.isError()) return response

        await new Promise((resolve) => setTimeout(resolve, 4000)) // wait for 4 seconds for the indexes to process the update before redirecting to the work order list page

        return Outcome.ok(undefined)
    }

    workOrderDismissChanged = (reason: string, workRequestsToDelete: number[]) =>
        this.update({
            workOrderDismissal: {
                reason: reason,
                workRequestsToDelete: workRequestsToDelete,
            },
        })

    workOrderDismissCancelled = () => this.update({ workOrderDismissal: null })

    dismissWorkRequest = async (
        workRequest: WorkRequest,
        reason: string,
        userId: number,
        tagChange: TagChange,
        tagReason: string
    ) => {
        const asset = this.state.asset
        const originalTagChange = tagChangeFromTag(asset?.tag ?? null)
        var updatedAsset = null

        if (asset !== null && originalTagChange !== tagChange) {
            updatedAsset = await this.assetService.updateTag(asset.id, tagChange, tagReason)
            if (updatedAsset.isError()) return updatedAsset

            this.assetChanged(updatedAsset.value)
        }

        const response = await this.workRequestService.dismissWorkRequest(
            workRequest.id,
            reason,
            userId,
            updatedAsset ? updatedAsset.value.tag : null
        )
        if (response.isError()) return response
        this._removeWorkRequest(workRequest.id)

        return Outcome.ok(undefined)
    }

    private _removeWorkRequest = (workRequestId: number) =>
        this.update({
            workRequests: this.state.workRequests.filter((it) => it.id !== workRequestId),
        })

    updateWorkRequest = (workRequests: WorkRequest[]) =>
        this.update({
            workRequests: workRequests,
        })

    updateShowMeterSync = (show: boolean) => this.update({ showSyncMeter: show })

    checkHaveBeenSyncBeforeServiceDate = () =>
        this.effect({
            id: WorkOrderEffect.Fetch,
            block: async (job) => {
                if (!this.state.asset) return Outcome.error("Asset has not been selected.")

                let response = await job.pause(
                    this.assetService.fetchMeterSyncRecordsAfterDate(
                        this.state.asset?.id,
                        this.state.form.completionDate!
                    )
                )
                if (response.isError()) {
                    return Outcome.error(response)
                }

                return Outcome.ok(response)
            },
        })

    syncModalHasBeenShown = (value: boolean) => {
        this.update({ syncModalHasBeenShown: value })
    }

    updateShowEmptyWorkOrderModal = (show: boolean) => this.update({ showEmptyWorkOrderModal: show })

    emptyWorkOrderModalConfirmed = (value: boolean) => this.update({ emptyWorkOrderModalHasBeenConfirmed: value })

    emptyWorkOrderModalDoNotShowAgainChanged = (value: boolean) =>
        this.update({ emptyWorkOrderModalDoNotShowAgain: value })

    updateMeterSyncRequired = (value: boolean) => {
        this.formFieldChanged({ meterSyncRequired: value })
    }

    callerFromMobileAppChanged = (callerFromMobileApp: string) =>
        this.update({ callerFromMobileApp: callerFromMobileApp })
}

function newWorkOrderViewState(): CreateNewWorkOrderScreenState {
    return {
        effectStatus: arrayToObject(Object.values(WorkOrderEffect), (it) => [it, AsyncStatus.idle()]),
        workOrder: null,
        asset: null,
        form: newWorkOrderForm(),
        formHasChanged: false,
        workRequests: [],
        hasBeenSaved: false,
        isSaving: false,
        isFormValid: false,
        ReassignWorkOrderModalIsVisible: false,
        showPrintList: false,
        workOrderToPrint: null,
        workOrderToPrintIsLoading: false,
        partsListIsLoading: false,
        pdfAsset: undefined,
        pdfWorkOrder: undefined,
        pdfAssetParts: [],
        pdfCompanyName: undefined,
        selectedPrintOption: undefined,
        showPartsList: false,
        showMeterSyncInfo: false,
        AssetParts: [],
        AssetSchedule: [],
        LatestAssetUsage: null,
        showOnHoldModal: false,
        workOrderHistory: [],
        isLoading: false,
        isVisible: false,
        isAfterSaveVisible: false,
        isEditModeLoading: false,
        isEditMode: false,
        completeModalOpen: false,
        closingModalOpen: false,
        workOrderDismissal: null,
        AssetUsagePriorToCompletion: null,
        closedWorkOrderEditMode: false,
        showSyncMeter: false,
        syncModalHasBeenShown: false,
        showEmptyWorkOrderModal: false,
        emptyWorkOrderModalHasBeenConfirmed: false,
        emptyWorkOrderModalDoNotShowAgain: false,
        callerFromMobileApp: null,
    }
}

/**
 * Resolves the Work Order's site for the form.
 *
 * The lookup list comes from fetchAllSites, which requests `Active=true`, so a Work Order pointing
 * at a site that has since been deactivated used to resolve to null. That is not just a display
 * gap: the upsert maps a null Site to SiteId = null, so saving such a Work Order silently cleared
 * its site. Falling back to the persisted SiteId keeps the value round-tripping, and keeps the
 * Required Fields check from reporting Site as missing on a Work Order that has one.
 *
 * The name is unavailable here (WorkOrder carries only siteId), so the placeholder tells the user
 * a site is set but is no longer selectable, rather than rendering an empty box that reads as "none".
 */
function siteForWorkOrder(workOrder?: WorkOrder | null, sites?: Site[] | null): Site | null {
    const siteId = workOrder?.siteId ?? null
    if (siteId === null) return null

    return (
        sites?.find((it) => it.id === siteId) ?? {
            id: siteId,
            name: `Site ${siteId} (inactive)`,
            description: "",
            companyId: 0,
        }
    )
}

export function newWorkOrderForm(
    workOrder?: WorkOrder | null,
    asset?: Asset | null,
    sites?: Site[] | null
): WorkOrderForm {
    return {
        workOrderId: workOrder?.id ?? null,
        status: workOrder?.status ?? WorkOrderStatus.Pending,
        assignedTo: workOrder?.assignedTo ?? 0,
        urgency: workOrder?.isUrgent ?? false,
        dueDate: workOrder?.dueDate ?? null,
        dueHourMeter: workOrder?.dueHourMeter ? Strings.formatDecimal(workOrder?.dueHourMeter ?? "") : null,
        dueOdometer: workOrder?.dueOdometer ? Strings.formatInteger(workOrder?.dueOdometer ?? "") : null,
        // Action Item #11164: null-check, not truthiness — a legitimately recorded 0 / 0.0 meter
        // must hydrate into the (now locked) field, or a closed-edit save would post "" and
        // silently wipe the stored reading.
        physicalOdometer:
            workOrder?.completedOdometer != null ? Strings.formatInteger(workOrder.completedOdometer) : null,
        physicalHourMeter:
            workOrder?.completedHourMeter != null ? Strings.formatDecimal(workOrder.completedHourMeter) : null,
        specialInstructions: workOrder?.specialInstructions ?? "",
        notifyContacts: workOrder?.notifyContacts ?? [],
        workPerformedById: workOrder?.workPerformedById ?? [],
        openedDate: workOrder?.openedDate ?? null,
        site: siteForWorkOrder(workOrder, sites),
        completionDate: workOrder?.workCompletedDate ?? null,
        onHold: workOrder?.onHold ?? false,
        onHoldReason: workOrder?.onHoldReason ?? null,
        meterSyncRequired: false,
        createdByID: workOrder?.createdByID ?? null,
        createdByUserData: workOrder?.createdByUserData ?? null,
    }
}

/**
 * Resolves the forward-link (childFollowUpId) for each item in the work order list.
 *
 * The backend stores followUpWorkRequestId as a backward link on the child item
 * (child.followUpWorkRequestId = parentId). This function cross-references to set
 * parentItem.childFollowUpId = childItem.id, so the UI can show "View Follow-Up".
 *
 * @param items       Work order items to update (the parents).
 * @param searchIn    Optional broader list to search for follow-up children.
 *                    Defaults to `items` itself when omitted (post-creation refresh case).
 *                    Pass unassigned asset work requests here for the initial load case,
 *                    since follow-ups have workOrderId = null and are not in `items`.
 */
export function resolveChildFollowUpIds(items: ServiceItemForm[], searchIn?: ServiceItemForm[]): void {
    const source = searchIn ?? items
    for (const candidate of source) {
        if (candidate.id != null && candidate.followUpWorkRequestId != null) {
            const parentItem = items.find((i) => i.id === candidate.followUpWorkRequestId)
            if (parentItem) {
                parentItem.childFollowUpId = candidate.id
            }
        }
    }
}

// Bug #11217: pick the value closer to due (sooner date / lower meter), ignoring nulls.
function closerDueDate(current: Date | null, incoming: Date | null): Date | null {
    if (incoming == null) return current
    if (current == null) return incoming
    return incoming.getTime() < current.getTime() ? incoming : current
}

function closerDueValue(current: number | null, incoming: number | null): number | null {
    if (incoming == null) return current
    if (current == null) return incoming
    return incoming < current ? incoming : current
}
